
import { Component, Inject, Prop, Provide, Vue } from "vue-property-decorator";
import {
  Control,
  OxygenControlsTreeContext,
  OxySelector
} from "@/oxygen/classes/lib/types";
import {
  updateSelectorProperties,
  createAutogeneratedSelectorAndActivate
} from "@/oxygen/classes/lib/classes";
import OxygenControl from "@/oxygen/classes/OxygenControl.vue";
import OxygenControls from "@/oxygen/classes/OxygenControls.vue";
import { configStore, uiStore } from "@/store/store";
import { ClassProperties as ClassPropertiesT } from "@/oxygen/classes/lib/type-properties";
import { classControls } from "@/oxygen/classes/lib/controls";
import defensiveCopy from "@/util/defensive-copy";
import lodash from "lodash";
import NiceButton from "@/components/UI/NiceButton.vue";
import {
  filterKeysByPrefix,
  getInheritanceMap
} from "@/oxygen/classes/lib/inheritance";
import { PanelContext } from "@/components/Builder/SidebarPanels.vue";

@Component({
  components: {
    NiceButton,
    OxygenControls,
    OxygenControl
  }
})
export default class ClassProperties extends Vue {
  controls: Control[] = classControls;
  editAnyway = false;

  @Provide("oxyContext")
  readonly context: OxygenControlsTreeContext = this.reactiveContext();

  @Inject({ from: "panelContext", default: undefined })
  readonly panelContext?: PanelContext;

  transactionKey: string | undefined | null = null;

  reactiveContext(): OxygenControlsTreeContext {
    return {
      getInheritanceMap: () => this.inheritanceMap,
      getControlInheritance: controlPath => this.inheritanceMap?.[controlPath],
      getControlInheritanceByPrefix: controlPath =>
        filterKeysByPrefix(this.inheritanceMap, `${controlPath}.`),
      getParentControlInheritance(controlPath) {
        const parentPath = this.getParentPath(controlPath);
        return filterKeysByPrefix(this.getInheritanceMap(), `${parentPath}.`);
      },
      getControlValue: controlPath => {
        const controlValue: Record<string, unknown> = {};

        for (const breakpoint of configStore.breakpoints) {
          const breakpointValue = lodash.get(
            this.properties,
            `${breakpoint.id}.${controlPath}`
          );

          if (breakpointValue !== undefined) {
            controlValue[breakpoint.id] = breakpointValue;
          }
        }

        return controlValue;
      },
      getControlValueAt: (controlPath, breakpoint) => {
        const fullPath = controlPath
          ? `${breakpoint}.${controlPath}`
          : breakpoint;

        return lodash.get(this.properties, fullPath);
      },
      getParentPath(controlPath) {
        return controlPath.split(".").slice(0, -1).join(".");
      },
      setControlValue: (controlPath, newValue, transactionKey) => {
        const newProperties = defensiveCopy(this.properties);
        const breakpoints = Object.keys(newValue);

        for (const breakpoint of breakpoints) {
          lodash.set(
            newProperties,
            `${breakpoint}.${controlPath}`,
            newValue[breakpoint]
          );
        }

        this.transactionKey = transactionKey;
        this.properties = newProperties;
      },
      setControlValueAt: (controlPath, breakpoint, value, transactionKey) => {
        const updatedProperties = defensiveCopy(this.properties);
        lodash.set(updatedProperties, `${breakpoint}.${controlPath}`, value);

        this.transactionKey = transactionKey;
        this.properties = updatedProperties;
      },
      getParentControlValueAt(controlPath, breakpoint) {
        const parentPath = this.getParentPath(controlPath);
        return this.getControlValueAt(parentPath, breakpoint);
      },
      getParentControlValue(controlPath) {
        const parentPath = this.getParentPath(controlPath);
        return this.getControlValue(parentPath);
      }
    };
  }

  @Prop()
  selector?: OxySelector;

  @Prop()
  nestedSelector?: OxySelector;

  get breakpoints() {
    return configStore.breakpoints;
  }

  get breakpointId() {
    return uiStore.currentBreakpoint.id;
  }

  get properties(): Record<string, ClassPropertiesT> {
    const source = this.nestedSelector || this.selector;

    if (!source || !source.properties) return {};
    return source.properties as Record<string, ClassPropertiesT>;
  }

  set properties(newProperties: Record<string, ClassPropertiesT>) {
    if (!this.selector) return;

    const transactionKey =
      this.transactionKey === null ? undefined : this.transactionKey;

    updateSelectorProperties({
      selectorId: this.selector.id,
      nestedSelectorId: this.nestedSelector?.id,
      properties: newProperties,
      transactionKey
    });
  }

  get propertiesAtCurrentBreakpoint() {
    return this.properties[this.breakpointId];
  }

  set propertiesAtCurrentBreakpoint(props: ClassPropertiesT) {
    const isEmptyObj = Object.keys(props).length === 0;
    const newProps = isEmptyObj ? null : props;

    const currentProperties: Record<string, ClassPropertiesT> =
      defensiveCopy(this.properties) || {};

    // @ts-ignore
    currentProperties[this.breakpointId] = newProps;

    if (!this.selector && this.element) {
      createAutogeneratedSelectorAndActivate(this.element, currentProperties);
      return;
    }

    if (this.selector) {
      this.properties = currentProperties;
    }
  }

  get element() {
    if (!uiStore.activeElement) return null;
    return uiStore.activeElement;
  }

  get inheritanceMap() {
    const currentSelector = this.nestedSelector?.id || this.selector?.id;

    if (!currentSelector) return undefined;

    // If nestedSelector is active, use it as the haystack.
    // If not in elementproperties panel, only include the current selector itself.
    // Otherwise, use the element's classes.
    let surroundingSelectors: string[];

    if (this.nestedSelector) {
      surroundingSelectors = [this.nestedSelector.id];
    } else if (this.panelContext?.state !== "elementproperties") {
      surroundingSelectors = [currentSelector];
    } else {
      surroundingSelectors =
        this.element?.data?.properties?.meta?.classes || [];
    }

    return getInheritanceMap(
      surroundingSelectors,
      currentSelector,
      this.breakpointId
    );
  }
}
